/**
 * families — `component_id` → its FAMILY, mirrored from
 * `backend/widget-native/registry.json`.
 *
 * The family is the one thing the registry says about a component that the KIT
 * entry does not: what KIND of thing it is (a dataviz module, a collection, a
 * control, page chrome). Layout decisions that must hold for EVERY component of
 * a kind — the dashboard's elevated panels are the first — read it here instead
 * of listing type names, so a component added to the registry is laid out
 * correctly without a second list being edited to match.
 *
 * It lives in its own module rather than in `registry.js` because `registry.js`
 * imports every component file: a component reading the family FROM registry.js
 * would close the import cycle. Nothing is imported here, so anyone may read it.
 *
 * `registerFamilies` overlays the SERVER registry the same way `registerRegistry`
 * overlays compositions — a family declared there wins, which is what keeps a
 * hot-shipped component laid out by its own declaration rather than by this
 * build's snapshot of the file. A drift test diffs this map against
 * registry.json.
 */

/** The registry's `family` for every component it declares. */
export const FAMILY = {
  // L1 primitives
  Text: 'content',
  Button: 'control',
  Icon: 'content',
  Image: 'media',
  Video: 'media',
  Monogram: 'content',
  Badge: 'content',
  Gauge: 'dataviz',
  Vertical: 'layout',
  Horizontal: 'layout',
  Stack: 'layout',
  Overlay: 'layout',
  Grid: 'layout',
  Surface: 'layout',
  Padding: 'layout',
  Divider: 'layout',
  CaptureButton: 'control',

  // L2 common
  SearchField: 'control',
  OptionSelect: 'control',
  ToolGrid: 'control',
  StatTileRow: 'dataviz',
  Sheet: 'chrome',
  EmptyState: 'status',
  ErrorState: 'status',
  PendingCard: 'collection',
  Card: 'collection',
  NoticeList: 'dataviz',
  MediaPicker: 'media',
  ScanEditor: 'media',
  BarChart: 'dataviz',
  SectionHeader: 'content',
  KeyValueList: 'content',
  TextBlock: 'content',
  Disclosure: 'content',
  Timeline: 'content',
  ModeSwitch: 'control',
  TextComposer: 'control',
  NumberField: 'control',
  RoleMediaForm: 'control',
  SlotBoard: 'layout',
  ConnectorLayer: 'dataviz',
  Carousel: 'media',
  BeforeAfterCompare: 'media',
  PageShell: 'chrome',
  ItemCollection: 'collection',
  SessionCollection: 'collection',
  ItemCalendar: 'collection',
  ItemPage: 'chrome',
  SessionPage: 'chrome',
  host: 'host',

  // L2 SESSION BLOCKS (v0.9 `native-ir`). No NEW family: a session block is
  // the same kind of thing as its widget-lane twin — a picture is `media`, a
  // figure row is `dataviz`, a list is `content`, a bar of buttons is
  // `control`. What none of them is, is `collection`: that family means the
  // sealed data lifecycle, and a block owns no feed, no identity and no CRUD.
  hero_media: 'media',
  before_after: 'media',
  image_gallery: 'media',
  video_player: 'media',
  // a document IS the asset this block is about — same family as the picture
  // and the video, and reached through the same two host verbs
  file_card: 'media',
  // …and a delegate is the ASSETS it promises: `media` for the same reason,
  // even while the only thing it can draw is the wait
  worker_delegate: 'media',
  text: 'content',
  markdown: 'content',
  agent_speech: 'content',
  table: 'content',
  chip_list: 'content',
  icon_list: 'content',
  numbered_steps: 'content',
  link_preview: 'content',
  // a place is what the block SAYS, not a picture it owns: the static map is an
  // illustration of the address, so it reads as `content` (registry.json agrees)
  location: 'content',
  shopping_list: 'content',
  stat_grid: 'dataviz',
  action_bar: 'control',

  // L3 composed
  CaloryRing: 'dataviz',
  WeekReview: 'dataviz',
};

/** Families declared by the installed server registry (hot-update wins). */
let REGISTERED_FAMILY = Object.create(null);

/**
 * The registry's OPTIONAL `skeleton` per component — the loading silhouette a
 * component wants when its family does not predict it. Nothing is bundled here
 * on purpose: the family answers for all 44 components today, and this is the
 * seam a hot-shipped one uses to disagree without a new web bundle.
 */
let REGISTERED_SKELETON = Object.create(null);

/** Install `registry.json`'s families — called by `registerRegistry`. */
export function registerFamilies(registryJson) {
  const next = Object.create(null);
  const skel = Object.create(null);
  for (const c of registryJson?.components || []) {
    if (!c?.id) continue;
    if (c.family) next[c.id] = c.family;
    if (c.skeleton) skel[c.id] = c.skeleton;
  }
  REGISTERED_FAMILY = next;
  REGISTERED_SKELETON = skel;
}

/** Forget the installed families; the bundled map remains. */
export function resetFamilies() {
  REGISTERED_FAMILY = Object.create(null);
  REGISTERED_SKELETON = Object.create(null);
}

/** A type's family, or null when nothing declares one. */
export function familyOf(type) {
  if (!type) return null;
  return REGISTERED_FAMILY[type] || FAMILY[type] || null;
}

/** A type's registry-declared skeleton shape, or null — see `skeletonShapeFor`. */
export function skeletonOf(type) {
  if (!type) return null;
  return REGISTERED_SKELETON[type] || null;
}
